import { useEffect, useMemo } from "react";

import { useAudienceState } from "@/context";
import { AudiencePageInput, deriveAudiencePageState } from "@/utils";
import { AudienceWorkflow } from "./useAudienceWorkflow";

type UseAudiencePageStateInput = Omit<AudiencePageInput, 'action' | 'currentDefinition'> & {
  workflow: Pick<AudienceWorkflow, 'action' | 'reset'>;
};

/**
 * The derived state of an audience detail page. Also clears the last action's
 * result when the audience is changed, so its errors and "just built" state
 * don't carry over.
 */
export const useAudiencePageState = ({
  workflow,
  audience,
  isNew,
  loading,
  loadError,
  audienceError,
  definition,
  rules,
}: UseAudiencePageStateInput) => {
  const { currentDefinition } = useAudienceState();
  const { action, reset } = workflow;

  const pageState = useMemo(() => deriveAudiencePageState({
    audience,
    isNew,
    loading,
    loadError,
    audienceError,
    definition,
    currentDefinition,
    action,
    rules,
  }), [audience, isNew, loading, loadError, audienceError, definition, currentDefinition, action, rules]);

  useEffect(() => {
    if (pageState.hasChanged) reset();
  }, [pageState.hasChanged, reset]);

  return pageState;
}
